import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import apiService from '../../services/api';
import { toast } from 'react-toastify';
import { FloorImageWrapper } from '../../styles/FloorImageWrapper';

const FloorImage: React.FC = () => {
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const previewURL = URL.createObjectURL(file);
    setImage(previewURL);
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiService.uploadFloorImage(formData);

      toast.success(response.message || 'Image saved successfully');
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';

      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const fetchFloorPlan = async () => {
    setLoading(true);
    try {
      const response = await apiService.getVenues();

      if (response?.floorImg?.imageUrl) {
        setImage(response?.floorImg?.imageUrl);
      }
    } catch (err: any) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFloorPlan();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* PAGE LOADER */}
      {loading && (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={45} />
        </Box>
      )}

      {!loading && (
        <>
          {/* HEADER */}
          <FloorImageWrapper>
            <Box className="header">
              <Typography sx={{ fontSize: 25, fontWeight: 600 }}>
                {' '}
                Floor Plan{' '}
              </Typography>

              <Button variant="contained" component="label" disabled={saving}>
                {saving
                  ? image
                    ? 'Editing...'
                    : 'Uploading...'
                  : image
                    ? 'Edit'
                    : 'Upload'}

                {/* Hide input while saving */}
                {!saving && (
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                )}
              </Button>
            </Box>

            {/* IMAGE AREA */}
            <Box className="image-area">
              {!image ? (
                <Typography>No floor image uploaded</Typography>
              ) : saving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress />
                  <Typography>Saving image...</Typography>
                </Box>
              ) : (
                <img src={image} alt="Floor Plan" className="floor-image" />
              )}
            </Box>
          </FloorImageWrapper>
        </>
      )}
    </Box>
  );
};

export default FloorImage;
